import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { MedioRecaudo, RecaudoResumen } from '@manejo-alcanza/contracts';
import {
  cambiarFacturacionEstricta,
  configuracionFacturacion,
  enviarReciboSiigo,
  fecha,
  listarFacturas,
  listarRecaudos,
  ordenesFacturables,
  pesos,
  registrarFactura,
  registrarRecaudo,
} from '../api';
import { Boton, Card, Cargando, Error_, Tabla, Tile } from '../componentes/basicos';
import { AvisosDeCobro } from '../componentes/AvisosDeCobro';
import { Conciliacion } from '../componentes/Conciliacion';
import { ImportarSiigo } from '../componentes/ImportarSiigo';

/**
 * La cartera: facturas y recaudos.
 *
 * **Esta es la pantalla que originó el proyecto.** Siigo sabe qué se facturó, el
 * banco sabe qué entró, y hasta ahora nadie podía explicar la diferencia. La
 * columna que hay que mirar es la última: *sin explicar*.
 *
 * Devengado y caja no se mezclan (invariante 13). Por eso son dos pestañas y no
 * una tabla con todo: el ingreso sale de lo que el cliente aceptó, la caja sale
 * del extracto, y juntarlas en la misma cifra es como una empresa cree que le va
 * bien el mes que le pagan lo del mes pasado.
 */
export function Facturas(): ReactNode {
  const [parametros, setParametros] = useSearchParams();
  const ver = parametros.get('ver');
  const pestana =
    ver === 'recaudos' || ver === 'avisos' || ver === 'conciliacion' ? ver : 'facturas';

  function ir(a: string): void {
    const nuevos = new URLSearchParams(parametros);
    if (a === 'facturas') nuevos.delete('ver');
    else nuevos.set('ver', a);
    setParametros(nuevos);
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">Plata</h1>
        <p className="mt-1 text-tenue">
          Lo que se facturó, lo que entró al banco, y por qué no son el mismo número.
        </p>
      </header>

      <div className="flex gap-1 border-b border-borde">
        <Pestana activa={pestana === 'facturas'} onClick={() => ir('facturas')}>
          Facturas
        </Pestana>
        {/* Entre facturas y avisos a proposito: es el paso que va en medio.
            Primero existe la factura, despues se sabe de que orden es, y solo
            entonces un aviso de cobro significa algo. */}
        <Pestana activa={pestana === 'conciliacion'} onClick={() => ir('conciliacion')}>
          Conciliación
        </Pestana>
        <Pestana activa={pestana === 'avisos'} onClick={() => ir('avisos')}>
          Avisos de cobro
        </Pestana>
        <Pestana activa={pestana === 'recaudos'} onClick={() => ir('recaudos')}>
          Recaudos
        </Pestana>
      </div>

      {pestana === 'facturas' && <PanelFacturas />}
      {pestana === 'conciliacion' && <Conciliacion />}
      {pestana === 'avisos' && <AvisosDeCobro />}
      {pestana === 'recaudos' && <PanelRecaudos />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PanelFacturas(): ReactNode {
  const [parametros, setParametros] = useSearchParams();
  const [creando, setCreando] = useState(false);
  const soloConDiferencia = parametros.get('diferencia') === '1';
  const soloVencidas = parametros.get('vencidas') === '1';

  const consulta = useQuery({
    queryKey: ['facturas', soloConDiferencia, soloVencidas],
    queryFn: () =>
      listarFacturas({
        ...(soloConDiferencia ? { soloConDiferencia: 'true' } : {}),
        ...(soloVencidas ? { soloVencidas: 'true' } : {}),
        tamano: '100',
      }),
    placeholderData: keepPreviousData,
  });

  function filtrar(clave: string, activo: boolean): void {
    const nuevos = new URLSearchParams(parametros);
    if (activo) nuevos.set(clave, '1');
    else nuevos.delete(clave);
    setParametros(nuevos);
  }

  if (consulta.isLoading) return <Cargando que="las facturas" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const datos = consulta.data!;
  const facturado = datos.datos.reduce((n, f) => n + Number(f.valorTotal), 0);
  const recaudado = datos.datos.reduce((n, f) => n + Number(f.recaudado), 0);
  const sinExplicar = datos.datos.reduce((n, f) => n + Number(f.noExplicada), 0);
  const vencidas = datos.datos.filter((f) => f.diasVencida !== null).length;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Tile etiqueta="Facturado" valor={pesos(String(facturado))} pie={`${datos.total} facturas`} />
        <Tile etiqueta="Entró al banco" valor={pesos(String(recaudado))} />
        <Tile
          etiqueta="Sin explicar"
          valor={pesos(String(sinExplicar))}
          pie="Ni una deducción registrada lo justifica"
        />
        <Tile
          etiqueta="Vencidas"
          valor={String(vencidas)}
          pie={vencidas === 0 ? 'ninguna en mora' : 'pasaron su fecha esperada'}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm">
          <input
            type="checkbox"
            checked={soloConDiferencia}
            onChange={(e) => filtrar('diferencia', e.target.checked)}
          />
          Solo con plata sin explicar
        </label>
        <label className="flex items-center gap-2 rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm">
          <input
            type="checkbox"
            checked={soloVencidas}
            onChange={(e) => filtrar('vencidas', e.target.checked)}
          />
          Solo vencidas
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <InterruptorFacturacion />
          <Boton onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Registrar factura'}
          </Boton>
        </div>
      </div>

      {creando && <FormularioFactura alTerminar={() => setCreando(false)} />}

      <ImportarSiigo />

      {datos.datos.length === 0 ? (
        <Card>
          <p className="text-sm text-tenue">
            No hay facturas registradas todavía. La factura se emite en Siigo; aquí se registra
            para poder compararla contra lo que el cliente recibió y contra lo que pagó.
          </p>
        </Card>
      ) : (
        <Tabla>
          <thead>
            <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
              <th className="px-3 py-2 font-medium">Número</th>
              <th className="px-3 py-2 font-medium">Cliente</th>
              <th className="px-3 py-2 font-medium">Emitida</th>
              <th className="px-3 py-2 font-medium">Se espera el</th>
              <th className="px-3 py-2 text-right font-medium">Facturado</th>
              <th className="px-3 py-2 text-right font-medium">Entró</th>
              <th className="px-3 py-2 text-right font-medium">Sin explicar</th>
            </tr>
          </thead>
          <tbody>
            {datos.datos.map((f) => (
              <tr key={f.id} className="border-b border-borde last:border-0 hover:bg-fondo">
                <td className="px-3 py-2">
                  <Link
                    to={`/plata/${f.id}`}
                    className="font-medium text-acento hover:underline"
                  >
                    {f.numero}
                  </Link>
                  {f.estado === 'ANULADA' && (
                    <div className="text-xs text-tenue">anulada</div>
                  )}
                </td>
                <td className="px-3 py-2">{f.clienteNombre}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fecha(f.fechaEmision)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {f.fechaEsperadaPago === null ? (
                    /* Sin saber cuándo la recibieron, el plazo no ha empezado a
                       correr. Decir un día sería inventarlo. */
                    <span className="text-tenue" title="Falta la fecha en que el cliente recibió la factura">
                      no se sabe
                    </span>
                  ) : (
                    <>
                      {fecha(f.fechaEsperadaPago)}
                      {f.diasVencida !== null && (
                        <span className="block text-xs text-alerta">
                          {f.diasVencida} días de mora
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="cifra px-3 py-2 font-medium">{pesos(f.valorTotal)}</td>
                <td className="cifra px-3 py-2 text-tenue">{pesos(f.recaudado)}</td>
                <td
                  className={`cifra px-3 py-2 ${
                    Number(f.noExplicada) === 0 ? 'text-tenue' : 'font-medium text-alerta'
                  }`}
                >
                  {pesos(f.noExplicada)}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * El interruptor de la facturación estricta.
 *
 * Prendida —lo de siempre— solo se factura lo que el cliente recibió. Apagada,
 * cualquier orden que no tenga ya una factura. Lo cambia el administrador; los
 * demás solo ven el aviso cuando está apagada, que es cuando cambia algo para
 * ellos. Quién puede cambiarlo lo dice la API, no esta pantalla.
 */
function InterruptorFacturacion(): ReactNode {
  const cliente = useQueryClient();
  const consulta = useQuery({
    queryKey: ['facturacion', 'configuracion'],
    queryFn: configuracionFacturacion,
  });
  const cambiar = useMutation({
    mutationFn: cambiarFacturacionEstricta,
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['facturacion'] }),
  });

  if (!consulta.data) return null;
  const { estricta, puedeCambiar } = consulta.data;

  if (!puedeCambiar) {
    return estricta ? null : (
      <span className="rounded-md border border-aviso/30 bg-aviso-tenue px-2 py-1.5 text-sm text-aviso">
        Facturación estricta apagada
      </span>
    );
  }

  return (
    <label
      className="flex items-center gap-2 rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
      title={
        estricta
          ? 'Solo se facturan órdenes entregadas'
          : 'Se factura cualquier orden que no tenga ya una factura'
      }
    >
      <input
        type="checkbox"
        checked={estricta}
        disabled={cambiar.isPending}
        onChange={(e) => cambiar.mutate(e.target.checked)}
      />
      Facturación estricta
      {cambiar.isError && <span className="text-alerta">— no se pudo cambiar</span>}
    </label>
  );
}

// ---------------------------------------------------------------------------

function FormularioFactura({ alTerminar }: { alTerminar: () => void }): ReactNode {
  const cliente = useQueryClient();
  const [numero, setNumero] = useState('');
  const [cufe, setCufe] = useState('');
  const [fechaEmision, setFechaEmision] = useState(hoy());
  const [fechaRecepcion, setFechaRecepcion] = useState('');
  const [valorTotal, setValorTotal] = useState('');
  const [ordenId, setOrdenId] = useState('');

  /*
   * Las órdenes que se ofrecen las decide la API, según el interruptor.
   *
   * Con la facturación estricta, solo las entregadas: facturar lo que el
   * cliente no ha recibido no es lo normal y ofrecerlo invita a hacerlo. Con
   * la estricta apagada, cualquiera que no tenga ya una factura.
   */
  const ordenes = useQuery({
    queryKey: ['facturacion', 'ordenes'],
    queryFn: ordenesFacturables,
  });
  const estricta = ordenes.data?.estricta ?? true;
  const facturables = ordenes.data?.ordenes ?? [];
  const elegida = facturables.find((o) => o.ordenId === ordenId);

  const guardar = useMutation({
    mutationFn: () =>
      registrarFactura({
        clienteId: elegida!.clienteId,
        numero: numero.trim(),
        ...(cufe.trim() ? { cufe: cufe.trim() } : {}),
        fechaEmision,
        ...(fechaRecepcion ? { fechaRecepcionCliente: fechaRecepcion } : {}),
        valorTotal: valorTotal.trim().replace(',', '.'),
        ordenes: [{ ordenId, valorImputado: valorTotal.trim().replace(',', '.') }],
      }),
    onSuccess: () => {
      alTerminar();
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
      // Con la estricta apagada, la orden recién facturada ya no se ofrece.
      void cliente.invalidateQueries({ queryKey: ['facturacion', 'ordenes'] });
    },
  });

  const listo =
    elegida !== undefined &&
    numero.trim() !== '' &&
    /^\d+(\.\d+)?$/.test(valorTotal.trim().replace(',', '.')) &&
    Number(valorTotal.replace(',', '.')) > 0;

  return (
    <Card titulo="Registrar una factura">
      <p className="mb-3 text-sm text-tenue">
        La factura se emite en <strong>Siigo</strong>, ante la DIAN. Aquí solo se registra, para
        poder compararla contra lo que el cliente recibió y contra lo que pagó.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Orden que se factura">
          <select
            value={ordenId}
            onChange={(e) => {
              setOrdenId(e.target.value);
              const o = facturables.find((x) => x.ordenId === e.target.value);
              // Se propone lo que el cliente aceptó, no lo que se pidió: es lo
              // que normalmente se factura. Si nadie lo ha registrado —solo
              // pasa con la estricta apagada— se propone lo pedido, y abajo se
              // dice. Se puede cambiar.
              if (o) setValorTotal(o.valorRecibido ?? o.valorPedido);
            }}
            className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="">
              {estricta ? 'Elige una orden entregada…' : 'Elige una orden sin factura…'}
            </option>
            {facturables.map((o) => (
              <option key={o.ordenId} value={o.ordenId}>
                {o.numero} · {o.clienteNombre} ·{' '}
                {o.valorRecibido === null
                  ? `pedido ${pesos(o.valorPedido)}, sin recepción`
                  : `recibido ${pesos(o.valorRecibido)}`}
              </option>
            ))}
          </select>
          {ordenes.isSuccess && facturables.length === 0 && (
            <span className="mt-1 block text-xs text-aviso">
              {estricta
                ? 'No hay órdenes entregadas todavía. Registra primero lo que recibió el cliente.'
                : 'Todas las órdenes vigentes ya tienen factura.'}
            </span>
          )}
          {elegida && elegida.valorRecibido === null && (
            <span className="mt-1 block text-xs text-aviso">
              Nadie ha registrado lo que recibió el cliente: el valor propuesto es lo pedido.
            </span>
          )}
        </Campo>

        <Campo etiqueta="Número de la factura">
          <input
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="FE-1234"
            className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </Campo>

        <Campo etiqueta="Valor total">
          <input
            inputMode="decimal"
            value={valorTotal}
            onChange={(e) => setValorTotal(e.target.value)}
            className="cifra w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </Campo>

        <Campo etiqueta="CUFE — opcional">
          <input
            value={cufe}
            onChange={(e) => setCufe(e.target.value)}
            placeholder="se puede completar después"
            className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </Campo>

        <Campo etiqueta="Fecha de emisión">
          <input
            type="date"
            value={fechaEmision}
            max={hoy()}
            onChange={(e) => setFechaEmision(e.target.value)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          />
        </Campo>

        <Campo etiqueta="Día en que el cliente la recibió — opcional">
          <input
            type="date"
            value={fechaRecepcion}
            max={hoy()}
            onChange={(e) => setFechaRecepcion(e.target.value)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          />
          {/* El plazo corre desde aquí, no desde la emisión. Sin esta fecha el
              sistema dice «no se sabe» en vez de inventar un día de pago. */}
          <span className="mt-1 block text-xs text-tenue">
            Desde aquí corren los {elegida?.tipo === '220' ? '30' : '45'} días del plazo. Sin
            ella, la fecha esperada de pago queda en «no se sabe».
          </span>
        </Campo>
      </div>

      {guardar.isError && (
        <div className="mt-3">
          <Error_>{(guardar.error as Error).message}</Error_>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <Boton onClick={() => guardar.mutate()} deshabilitado={!listo || guardar.isPending}>
          Registrar
        </Boton>
        <Boton tipo="secundario" onClick={alTerminar}>
          Cancelar
        </Boton>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function PanelRecaudos(): ReactNode {
  const cliente = useQueryClient();
  const [creando, setCreando] = useState(false);

  const consulta = useQuery({
    queryKey: ['recaudos'],
    queryFn: () => listarRecaudos({ tamano: '100' }),
  });
  const facturas = useQuery({
    queryKey: ['facturas', 'para-aplicar'],
    queryFn: () => listarFacturas({ tamano: '200' }),
  });

  const [fechaRecaudo, setFechaRecaudo] = useState(hoy());
  const [medio, setMedio] = useState<MedioRecaudo>('TRANSFERENCIA');
  const [valor, setValor] = useState('');
  const [referencia, setReferencia] = useState('');
  const [facturaId, setFacturaId] = useState('');

  const abiertas = (facturas.data?.datos ?? []).filter(
    (f) => f.estado === 'EMITIDA' && Number(f.noExplicada) > 0,
  );
  const elegida = abiertas.find((f) => f.id === facturaId);

  const guardar = useMutation({
    mutationFn: () =>
      registrarRecaudo({
        clienteId: elegida!.clienteId,
        fechaRecaudo,
        medio,
        valorRecibido: valor.trim().replace(',', '.'),
        ...(referencia.trim() ? { referenciaSoporte: referencia.trim() } : {}),
        facturas: [{ facturaId, valorAplicado: valor.trim().replace(',', '.') }],
      }),
    onSuccess: () => {
      setCreando(false);
      setValor('');
      setReferencia('');
      setFacturaId('');
      void cliente.invalidateQueries({ queryKey: ['recaudos'] });
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
    },
  });

  if (consulta.isLoading) return <Cargando que="los recaudos" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const datos = consulta.data!;
  const entro = datos.datos.reduce((n, r) => n + Number(r.valorRecibido), 0);
  const sinAplicar = datos.datos.reduce((n, r) => n + Number(r.sinAplicar), 0);

  const listo =
    elegida !== undefined &&
    /^\d+(\.\d+)?$/.test(valor.trim().replace(',', '.')) &&
    Number(valor.replace(',', '.')) > 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Tile etiqueta="Entró al banco" valor={pesos(String(entro))} pie={`${datos.total} recaudos`} />
        <Tile
          etiqueta="Sin repartir"
          valor={pesos(String(sinAplicar))}
          pie="Plata que llegó y no se sabe a qué factura corresponde"
        />
        <div className="flex items-center justify-end">
          <Boton onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Registrar recaudo'}
          </Boton>
        </div>
      </div>

      {creando && (
        <Card titulo="Registrar un pago">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Factura que cancela">
              <select
                value={facturaId}
                onChange={(e) => {
                  setFacturaId(e.target.value);
                  const f = abiertas.find((x) => x.id === e.target.value);
                  if (f) setValor(f.noExplicada);
                }}
                className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              >
                <option value="">Elige una factura…</option>
                {abiertas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.numero} · {f.clienteNombre} · pendiente {pesos(f.noExplicada)}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta="Valor que entró">
              <input
                inputMode="decimal"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                className="cifra w-full rounded-md border border-borde px-2 py-1.5 text-sm"
              />
              {/* Lo que dice el extracto, no una cuenta hecha. Los descuentos se
                  registran aparte, cada uno con su categoría. */}
              <span className="mt-1 block text-xs text-tenue">
                Lo que dice el extracto. Los descuentos van aparte, en la factura.
              </span>
            </Campo>
            <Campo etiqueta="Medio">
              <select
                value={medio}
                onChange={(e) => setMedio(e.target.value as MedioRecaudo)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              >
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="CHEQUE">Cheque</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="COMPENSACION">Compensación</option>
                <option value="OTRO">Otro</option>
              </select>
            </Campo>
            <Campo etiqueta="Fecha">
              <input
                type="date"
                value={fechaRecaudo}
                max={hoy()}
                onChange={(e) => setFechaRecaudo(e.target.value)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              />
            </Campo>
            <Campo etiqueta="Referencia del soporte — opcional">
              <input
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                placeholder="número de la consignación"
                className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
              />
            </Campo>
          </div>

          {guardar.isError && (
            <div className="mt-3">
              <Error_>{(guardar.error as Error).message}</Error_>
            </div>
          )}

          <div className="mt-4">
            <Boton onClick={() => guardar.mutate()} deshabilitado={!listo || guardar.isPending}>
              Registrar
            </Boton>
          </div>
        </Card>
      )}

      {datos.datos.length === 0 ? (
        <Card>
          <p className="text-sm text-tenue">
            Todavía no hay pagos registrados. Esta es la mitad de caja del sistema: lo que entró
            al banco, que no es lo mismo que lo que se facturó.
          </p>
        </Card>
      ) : (
        <Tabla>
          <thead>
            <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
              <th className="px-3 py-2 font-medium">Fecha</th>
              <th className="px-3 py-2 font-medium">Cliente</th>
              <th className="px-3 py-2 font-medium">Medio</th>
              <th className="px-3 py-2 font-medium">Cubre</th>
              <th className="px-3 py-2 text-right font-medium">Entró</th>
              <th className="px-3 py-2 text-right font-medium">Sin repartir</th>
              <th className="px-3 py-2 font-medium">Recibo en Siigo</th>
            </tr>
          </thead>
          <tbody>
            {datos.datos.map((r) => (
              <tr key={r.id} className="border-b border-borde last:border-0">
                <td className="px-3 py-2 whitespace-nowrap">{fecha(r.fechaRecaudo)}</td>
                <td className="px-3 py-2">{r.clienteNombre}</td>
                <td className="px-3 py-2">
                  {r.medio.toLowerCase()}
                  {r.referenciaSoporte && (
                    <span className="block font-mono text-xs text-tenue">
                      {r.referenciaSoporte}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.facturas.length === 0 ? (
                    <span className="text-aviso">todavía no se sabe</span>
                  ) : (
                    r.facturas.map((f) => (
                      <Link
                        key={f.facturaId}
                        to={`/plata/${f.facturaId}`}
                        className="mr-2 text-acento hover:underline"
                      >
                        {f.numero}
                      </Link>
                    ))
                  )}
                </td>
                <td className="cifra px-3 py-2 font-medium">{pesos(r.valorRecibido)}</td>
                <td
                  className={`cifra px-3 py-2 ${
                    Number(r.sinAplicar) === 0 ? 'text-tenue' : 'text-aviso'
                  }`}
                >
                  {pesos(r.sinAplicar)}
                </td>
                <td className="px-3 py-2 text-xs">
                  <ReciboDeCaja recaudo={r} />
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Mandar el recibo de caja a Siigo, o decir que ya se mandó.
 *
 * **Es un paso aparte de confirmar el pago, a propósito** *(E3-08)*: escribir en
 * la contabilidad de otro sistema no se hace de paso. Y es idempotente — pulsar
 * dos veces no crea dos recibos — así que reintentar cuando algo falla es
 * seguro.
 */
function ReciboDeCaja({ recaudo }: { recaudo: RecaudoResumen }): ReactNode {
  const cliente = useQueryClient();
  const [enviado, setEnviado] = useState<string | null>(null);

  const enviar = useMutation({
    mutationFn: () => enviarReciboSiigo(recaudo.id),
    onSuccess: (r) => {
      setEnviado(r.reciboNumero ?? r.reciboId);
      void cliente.invalidateQueries({ queryKey: ['recaudos'] });
    },
  });

  if (enviado !== null) {
    return <span className="font-mono text-xs text-acento">{enviado}</span>;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => enviar.mutate()}
        disabled={enviar.isPending}
        className="text-acento underline hover:no-underline disabled:opacity-50"
      >
        {enviar.isPending ? 'mandando…' : 'mandar a Siigo'}
      </button>
      {enviar.isError && (
        <span className="mt-1 block text-alerta">{(enviar.error as Error).message}</span>
      )}
    </>
  );
}

function Pestana({
  activa,
  onClick,
  children,
}: {
  activa: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm ${
        activa
          ? 'border-acento font-medium text-acento'
          : 'border-transparent text-tenue hover:text-texto'
      }`}
    >
      {children}
    </button>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tenue">{etiqueta}</span>
      {children}
    </label>
  );
}

/** Colombia es siempre UTC-5. */
function hoy(): string {
  return new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
